import { MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Dropdown, Flex, Menu, Modal } from "antd";
import isBetween from "dayjs/plugin/isBetween";
import { FC, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import {
  graySmallCircle,
  orangeSmallCircle,
  primarySmallCircle,
} from "../../../assets/SVGs/SVGs";
import { getClosedPeriods } from "../../../network/settings";
import { getTimeOffs } from "../../../network/timeOff";
import { getWorkingHours } from "../../../network/workingHours";
import { queryKeys } from "../../../services/react-query/queryKeys";
import { location } from "../../../types/calendar";
import {
  formateTimeToAMPM,
  getFormatedDayDate,
  getWeekDays,
  weekEnd,
  weekStart,
} from "../../../utils/DateUtils";
import AddAddtionalShift from "./AddAdditionalShift";
import DeleteTimeOff from "./DeleteTimeOff";
import DeleteWorkingShift from "./DeleteWorkingShift";
import AddBreakForm from "./addShift/AddBreakForm";
import Main from "./addShift/Main";

interface CalendarBodyProps {
  teamMemberData: any;
  locationId: number;
  selectedDate: Date;
}
export type workingHour = {
  date: string;
  start_time: string;
  end_time: string;
  id: number;
  off: 0 | 1;
  breaks: {
    from: string;
    to: string;
    typeId: number;
    description: string;
    approved: 0 | 1;
    id: number;
  }[];
  additional_shifts: { start_time: string; end_time: string; id: number }[];
  workDuration?: {
    hours: number;
    minutes: number;
  };
};

const CalendarBody: FC<CalendarBodyProps> = ({
  teamMemberData,
  locationId,
  selectedDate: currentSelectedDate,
}) => {
  const intl = useIntl();
  const [formatedWorkingHours, setFormatedWorkingHours] = useState<
    Array<workingHour | null>
  >([]);
  const { currentLang } = useSelector(
    (state: { user: { currentLang: "en" | "ar" } }) => state.user
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const dayjs = require("dayjs");

  const { data: closedPeriods, isLoading } = useQuery(
    [queryKeys?.listClosedPeriods],
    () => getClosedPeriods(),
    {
      select: (res) => {
        const activeLocationClosedPeriods = res?.data?.filter(
          (closedPeriod: any) => {
            return (
              closedPeriod?.locations?.find(
                (loc: location) => loc.id === locationId
              ) !== undefined
            );
          }
        );

        return activeLocationClosedPeriods;
      },
    }
  );

  const { data: workingHours } = useQuery(
    [queryKeys.workingHours, locationId, currentSelectedDate],
    () =>
      getWorkingHours(
        locationId,
        teamMemberData.id,
        weekStart(currentSelectedDate),
        weekEnd(currentSelectedDate)
      ),
    {
      select: (res) => {
        return res?.data;
      },
    }
  );
  const { data: vacations } = useQuery(
    [queryKeys.timeOffs, locationId],
    () => getTimeOffs(teamMemberData.id),
    {
      select: (res) =>
        res?.data?.map((day: { date: string }) => ({
          date: dayjs(day?.date).format("YYYY-MM-DD"),
          off: 1,
        })),
    }
  );

  const getWorkDuration = (day: workingHour) => {
    const start = dayjs(day?.start_time || "09:00:00", "HH:mm:ss");
    const end = dayjs(day?.end_time || "18:00:00", "HH:mm:ss");
    if (!start.isValid() || !end.isValid()) {
      throw new Error("Invalid start or end time format. Please use HH:mm:ss.");
    }

    // Handle times crossing midnight gracefully
    if (end.isBefore(start)) {
      end.add(1, "day");
    }

    // Calculate duration from main start and end times
    const mainDuration = end.diff(start, "minutes");

    // Calculate durations for each additional shift
    const additional = day?.additional_shifts || [];
    const additionalShiftDurations = additional
      .map((shift) => {
        // Validate additional shift times
        const shiftStart = dayjs(shift.start_time, "HH:mm:ss");
        const shiftEnd = dayjs(shift.end_time, "HH:mm:ss");

        if (!shiftStart.isValid() || !shiftEnd.isValid()) {
          throw new Error(
            "Invalid additional shift time format. Please use HH:mm:ss."
          );
        }

        // Account for midnight crossing within shifts
        if (shiftEnd.isBefore(shiftStart)) {
          shiftEnd.add(1, "day");
        }

        return shiftEnd.diff(shiftStart, "minutes");
      })
      .reduce((accumulator, duration) => accumulator + duration, 0);

    // Combine main and additional shift durations
    const totalDuration = mainDuration + additionalShiftDurations;

    const hours = Math.floor(totalDuration / 60);
    const minutes = totalDuration % 60;

    return { hours: hours || 0, minutes: minutes || 0 };
  };

  const formatWorkingHours = () => {
    if (!Array.isArray(workingHours)) {
      return;
    }
    let regularShifts: any[] = [];
    const additionalShifts: any[] = [];
    workingHours?.forEach((item: { shift_type: string }) => {
      if (item?.shift_type === "regular") {
        regularShifts.push(item);
      } else if (item?.shift_type === "additional") {
        additionalShifts.push(item);
      }
    });
    if (vacations && vacations?.length > 0) {
      regularShifts = [...regularShifts, ...vacations];
    }
    const days = getWeekDays(currentSelectedDate);
    const newWorkingHours: Array<workingHour | null> = days?.map((day) => {
      const isVacationDay =
        vacations?.length > 0
          ? vacations?.find((vacation: { date: string }) => {
              return (
                dayjs(vacation?.date).format("YYYY-MM-DD") ===
                dayjs(day?.dayDate).format("YYYY-MM-DD")
              );
            })
          : null;
      const workingHour = isVacationDay
        ? isVacationDay
        : regularShifts?.find((workingHour: any) => {
            return (
              dayjs(day.dayDate).format("YYYY-MM-DD") ===
              dayjs(workingHour?.date).format("YYYY-MM-DD")
            );
          });
      if (workingHour) {
        workingHour.additional_shifts = additionalShifts.filter(
          (shift: any) => {
            return shift.date === workingHour.date;
          }
        );
      }
      return workingHour ? workingHour : null;
    });
    const formatedWithWorkDuration = newWorkingHours?.map((day) => {
      if (day) {
        return {
          ...day,
          workDuration: getWorkDuration(day),
        };
      }
      return null;
    });
    if (
      JSON.stringify(formatedWithWorkDuration) ===
      JSON.stringify(formatedWorkingHours)
    ) {
      return;
    }
    setFormatedWorkingHours(formatedWithWorkDuration);
  };
  dayjs.extend(isBetween);
  useEffect(() => {
    formatWorkingHours();
  }, [workingHours, vacations]);

  const renderBreakTime = (
    breaksOnDay: {
      id: number;
      from: string;
      to: string;
      approved: 1 | 0;
      description: string | null;
    }[]
  ) => {
    return breaksOnDay.map((timeOff) => (
      <div
        className="bg-[#9999A7] text-white rounded-md w-full mb-2 py-3 px-1"
        key={timeOff.id}
      >
        <Flex justify="flex-end" className="mb-0 p-0">
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="delete"
                  onClick={() => openModal("DeleteTimeOff", String(timeOff.id))}
                >
                  {intl.formatMessage({
                    id: "delete",
                    defaultMessage: "حذف",
                  })}
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined />
          </Dropdown>
        </Flex>
        <p className="text-center mb-1">
          {intl.formatMessage({
            id: "break",
            defaultMessage: "فترة استراحة",
          })}
        </p>
        <Flex justify="center">
          <p className="flex gap-1 items-center">
            <small className="text-xs self-center mt-2">
              {formateTimeToAMPM(timeOff?.from)}
              {/* {dayjs(
                dateFormatter + " " + timeOff?.from,
                "DD/MM/YYYY HH:mm:ss"
              ).format("HH:mm a")} */}
            </small>
          </p>
          <small className="text-xs self-center mt-2"> - </small>
          <p className="flex gap-1 items-center">
            <small className="text-xs self-center mt-2">
              {formateTimeToAMPM(timeOff?.to)}
              {/* {dayjs(
                dateFormatter + " " + timeOff?.to,
                "DD/MM/YYYY HH:mm:ss"
              ).format("HH:mm a")} */}
            </small>
          </p>
        </Flex>
      </div>
    ));
  };

  const renderRegularShifts = (regularShift: workingHour | null) => {
    if (!regularShift) return null;

    return (
      <div
        className="bg-[#00086D] text-white rounded-md w-full mb-2 py-3 px-1"
        key={regularShift?.id}
      >
        <Flex justify="flex-end" className="mb-0 p-0">
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="delete"
                  onClick={() =>
                    openModal("DeleteWorkingShift", String(regularShift?.id))
                  }
                >
                  {intl.formatMessage({
                    id: "delete",
                    defaultMessage: "حذف",
                  })}
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined />
          </Dropdown>
        </Flex>
        <p className="text-center mb-1">
          {intl.formatMessage({
            id: "workingShift",
            defaultMessage: "دوام كامل",
          })}
        </p>
        <Flex justify="center">
          <p className="flex gap-1 items-center">
            <small className="text-xs self-center mt-2">
              {formateTimeToAMPM(regularShift?.start_time)}
              {/* {dayjs(
                dateFormatter + " " + regularShift?.start_time,
                "DD/MM/YYYY HH:mm:ss"
              ).format("HH:mm a")} */}
            </small>
          </p>
          <small className="text-xs self-center mt-2 "> - </small>
          <p className="flex gap-1 items-center">
            <small className="text-xs self-center mt-2">
              {formateTimeToAMPM(regularShift?.end_time)}
              {/* {dayjs(
                dateFormatter + " " + regularShift?.end_time,
                "DD/MM/YYYY HH:mm:ss"
              ).format("HH:mm a")} */}
            </small>
          </p>
        </Flex>
      </div>
    );
  };

  const renderAdditionalShifts = (
    additionalShifts: {
      id: number;
      start_time: string;
      end_time: string;
    }[]
  ) => {
    return (
      additionalShifts &&
      additionalShifts?.map((shift) => (
        <div
          className="bg-[#CF953D] text-white rounded-md w-full mb-2 py-3 px-1 h-[]"
          key={shift?.id}
        >
          <Flex justify="flex-end" className="mb-0 p-0">
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="delete"
                    onClick={() =>
                      openModal("DeleteWorkingShift", String(shift?.id))
                    }
                  >
                    {intl.formatMessage({
                      id: "delete",
                      defaultMessage: "حذف",
                    })}
                  </Menu.Item>
                </Menu>
              }
            >
              <MoreOutlined />
            </Dropdown>
          </Flex>

          <p className="text-center mb-1">
            {intl.formatMessage({
              id: "additionalShift",
              defaultMessage: "دوام جزئي",
            })}
          </p>
          <Flex justify="center">
            <p className="flex gap-1 items-center">
              <small className="text-xs self-center mt-2">
                {formateTimeToAMPM(shift?.start_time)}
              </small>
            </p>
            <small className="text-xs self-center mt-2 "> - </small>
            <p className="flex gap-1 items-center">
              <small className="text-xs self-center mt-2">
                {formateTimeToAMPM(shift?.end_time)}
              </small>
            </p>
          </Flex>
        </div>
      ))
    );
  };

  const [activeModal, setActiveModal] = useState(null);

  const openModal = (modal: any, selectedDate: string | null = null) => {
    setSelectedDate(selectedDate);
    setActiveModal(modal);
  };

  const closeModal = () => {
    setActiveModal(null);
  };
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const modalComponents = {
    AddBreak: (
      <Modal width={600} visible={true} onCancel={closeModal} footer={null}>
        <AddBreakForm
          locationId={locationId}
          teamMemberId={teamMemberData?.id}
          shifts={formatedWorkingHours}
          breakIndex={1}
          handleDeleteBreak={() => {}}
          handleClose={closeModal}
          handleAddBreak={() => {}}
          index={1}
          selectedDate={selectedDate!}
          dayBreak={{
            teamMemberId: teamMemberData?.id,
            locationId,
          }}
        />
      </Modal>
    ),
    AddAddtionalShift: (
      <AddAddtionalShift
        visible={true}
        onClose={closeModal}
        team_member_id={teamMemberData?.id}
        selectedDate={selectedDate}
        location_id={locationId}
      />
    ),
    AddShifts: (
      <Main
        visible={true}
        onClose={closeModal}
        team_member_id={teamMemberData?.id}
        location_id={locationId}
        selectedDate={selectedDate}
      />
    ),
    DeleteWorkingShift: (
      <DeleteWorkingShift
        locationId={locationId}
        teamMemberId={teamMemberData?.id}
        visible={true}
        onClose={closeModal}
        workingShiftId={selectedDate}
      />
    ),
    DeleteTimeOff: (
      <DeleteTimeOff
        visible={true}
        onClose={closeModal}
        timeOffId={selectedDate}
        teamMemberId={teamMemberData?.id}
        locationId={locationId}
      />
    ),
  };

  const renderModal = activeModal ? modalComponents[activeModal] : null;

  const isSalonLocationClosed = (date: string) => {
    let flag = false;
    closedPeriods?.forEach(
      (closedPeriod: {
        id: number;
        start_date: string;
        end_date: string;
        locations: location[];
      }) => {
        if (
          dayjs(date).isSame(dayjs(closedPeriod?.start_date)) ||
          dayjs(date).isBetween(
            dayjs(closedPeriod?.start_date),
            dayjs(closedPeriod?.end_date)
          ) ||
          dayjs(date).isSame(dayjs(closedPeriod?.end_date))
        ) {
          flag = true;
        } else {
        }
      }
    );
    return flag;
  };
  return (
    <div
      ref={containerRef}
      className="isolate rounded-md flex flex-auto flex-col overflow-auto bg-white "
    >
      <div className=" flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full bg-[#FBFBFF] p-3">
        {renderModal}
        <div className="col-start-1 col-end-2 row-start-1  grid-cols-7 grid-rows-1 sm:grid sm:grid-cols-7 gap-4 md:gap-2 lg:gap-2 flex min-w-[900px]">
          {getWeekDays(currentSelectedDate)?.map((day, index) => (
            <div key={index} className="min-w-[140px]">
              <div className=" max-h-[100px] pt-1 flex flex-col items-center justify-center rounded  gap-1 bg-[#F2F3FA]">
                <div className="items-center justify-center  bg-[#EEF0FF] mb-3">
                  <p className="font-semibold text-center text-sm">
                    {getFormatedDayDate(day.dayDate, currentLang)}
                  </p>
                </div>
                <div className="bg-white p-1 rounded">
                  {formatedWorkingHours[index]?.off === 1 ||
                  formatedWorkingHours[index] === null ? (
                    <p className="flex justify-center gap-1">
                      <span className=" text-color-primary ">
                        0
                        <small className="text-xs mx-1 text-color-sec-title self-end">
                          {intl.formatMessage({
                            id: "hours",
                            defaultMessage: "ساعات",
                          })}
                        </small>
                      </span>
                      <span className=" text-color-primary">
                        0
                        <small className="text-xs  mx-1 text-color-sec-title self-end">
                          {intl.formatMessage({
                            id: "minutes",
                            defaultMessage: "دقيقة",
                          })}
                        </small>
                      </span>
                    </p>
                  ) : (
                    <p className="flex justify-center gap-1">
                      <span className=" text-color-primary">
                        {formatedWorkingHours[index]?.workDuration?.hours}
                        <small className="text-xs  mx-1 text-color-sec-title self-end">
                          {intl.formatMessage({
                            id: "hours",
                            defaultMessage: "ساعات",
                          })}
                        </small>
                      </span>
                      <span className=" text-color-primary">
                        {formatedWorkingHours[index]?.workDuration?.minutes}
                        <small className="text-xs  mx-1 text-color-sec-title self-end">
                          {intl.formatMessage({
                            id: "minutes",
                            defaultMessage: "دقيقة",
                          })}
                        </small>
                      </span>
                    </p>
                  )}
                </div>
                <div className="h-5">
                  {formatedWorkingHours[index] !== null &&
                  formatedWorkingHours[index]?.off !== 1 ? (
                    <p className="flex justify-center gap-1 ">
                      <span>
                        {formatedWorkingHours[index]?.off === 0 &&
                        formatedWorkingHours[index]?.start_time &&
                        formatedWorkingHours[index]?.start_time
                          ? primarySmallCircle
                          : null}
                      </span>
                      <span>
                        {" "}
                        {formatedWorkingHours[index]?.off === 0 &&
                        formatedWorkingHours[index]?.breaks?.length
                          ? graySmallCircle
                          : null}
                      </span>
                      <span>
                        {" "}
                        {formatedWorkingHours[index]?.additional_shifts?.length
                          ? orangeSmallCircle
                          : null}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-col items-center justify-center p-2 bg-white ">
                {formatedWorkingHours[index] === null ||
                formatedWorkingHours[index]?.off === 1 ? null : (
                  <>
                    {!isSalonLocationClosed(day?.dayDate) ? (
                      <>
                        {renderRegularShifts(formatedWorkingHours[index])}

                        {formatedWorkingHours[index]?.breaks?.length
                          ? renderBreakTime(
                              formatedWorkingHours[index]?.breaks || []
                            )
                          : null}

                        {formatedWorkingHours[index]?.additional_shifts?.length
                          ? renderAdditionalShifts(
                              formatedWorkingHours[index]?.additional_shifts ||
                                []
                            )
                          : null}
                      </>
                    ) : null}
                  </>
                )}

                <Dropdown
                  disabled={
                    formatedWorkingHours[index]?.off === 1 ||
                    dayjs(day.dayDate, "YYYY-MM-DD").isBefore(
                      dayjs().format("YYYY-MM-DD")
                    ) ||
                    isSalonLocationClosed(day?.dayDate)
                  }
                  className="float-left block"
                  overlay={
                    <Menu>
                      <Menu.Item
                        key="2"
                        disabled={!!formatedWorkingHours[index]?.start_time}
                        onClick={() => openModal("AddShifts", day.dayDate)}
                        className="border-none bg-transparent shadow-none outline-none"
                      >
                        <p className="flex gap-1 ">
                          <span className="text-color-primary font-bold">
                            +
                          </span>

                          <span>
                            {intl.formatMessage({
                              id: "workingShift",
                              defaultMessage: "دوام كامل",
                            })}
                          </span>
                        </p>
                      </Menu.Item>
                      <Menu.Item
                        key="3"
                        disabled={!!!formatedWorkingHours[index]?.start_time}
                        onClick={() =>
                          openModal("AddAddtionalShift", day.dayDate)
                        }
                        className="border-none bg-transparent shadow-none outline-none"
                      >
                        <p className="flex gap-1 ">
                          <span className="text-color-primary font-bold">
                            +
                          </span>

                          <span>
                            {" "}
                            {intl.formatMessage({
                              id: "additionalShift",
                              defaultMessage: "دوام إضافي",
                            })}
                          </span>
                        </p>
                      </Menu.Item>
                      <Menu.Item
                        key="1"
                        disabled={!!!formatedWorkingHours[index]?.start_time}
                        onClick={() => openModal("AddBreak", day.dayDate)}
                        className="border-none bg-transparent shadow-none outline-none"
                      >
                        <p className="flex gap-1 ">
                          <span className="text-color-primary font-bold">
                            +
                          </span>

                          <span>
                            {" "}
                            {intl.formatMessage({
                              id: "break",
                              defaultMessage: "فترة استراحة",
                            })}
                          </span>
                        </p>
                      </Menu.Item>
                    </Menu>
                  }
                >
                  <Button
                    style={{ maxHeight: "12px" }}
                    block
                    className="border-none bg-[#FBFBFF] shadow-none outline-none"
                    icon={
                      <PlusOutlined style={{ width: "12px", height: "12px" }} />
                    }
                  ></Button>
                </Dropdown>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarBody;
